<template>
  <div ref="containerRef" class="date-picker-container">
    <button
      v-if="!isDatePickerOpen && !autoOpen"
      ref="displayButtonRef"
      class="date-picker-display"
      :class="[
        {
          'date-picker-display-open': isOpen,
          'disabled-picker': isDisableDatepicker,
          'date-picker-display-dirty': isDirty,
        },
      ]"
      @click="togglePicker"
      :disabled="isDisableDatepicker"
    >
      <Icon
        :name="
          customCalendarIcon
            ? 'calendar_icon'
            : changeBadgeIndicator
            ? 'badge_has_expire'
            : 'badge_indicator'
        "
      />
      <span>{{ formattedDate || dataPickerPlaceHolder }}</span>
      <img
        v-if="showClearIcon && formattedDate"
        class="append-icon-hover"
        @click.stop="clearSelection"
        :src="require('@components/assets/x-circle.svg')"
        alt="icon"
      />
    </button>

    <!-- Calendar as modal or dropdown -->
    <div v-if="isOpen">
      <div
        v-if="modal"
        ref="modalOverlayRef"
        class="date-picker-modal-overlay date-picker-modal-overlay--programmatic"
        @click.self="finishPicker"
      >
        <div class="date-picker-modal">
          <div class="date-picker-header">
            <div
              class="date-picker-icon"
              :class="{ 'disabled-icon': isPrevButtonDisabled }"
            >
              <img
                :src="require('@components/assets/left-icon-dark.svg')"
                alt=""
                class="nav-btn"
                @click="handlePrevMonth"
                :class="{ 'disabled-btn': isPrevButtonDisabled }"
              />
            </div>
            <span class="month-year">{{ monthYear }}</span>
            <div
              class="date-picker-icon"
              :class="{ 'disabled-icon': isNextButtonDisabled }"
            >
              <img
                :src="require('@components/assets/right-icon-dark.svg')"
                alt=""
                class="nav-btn"
                @click="handleNextMonth"
                :class="{ 'disabled-btn': isNextButtonDisabled }"
              />
            </div>
          </div>
          <div class="date-picker-search-bar">
            <ExButton
              class="mr-2"
              :outline="'outline-hover'"
              :label="'Today'"
              @onClick="selectToday"
              size="normal"
              :fullwidth="'default'"
              :normal="isTodayDisabled ? 'normal-disable' : 'normal-hover'"
            />
          </div>
          <div class="date-picker-calendar">
            <div class="date-picker-weekdays">
              <span v-for="day in weekdays" :key="day" class="weekday">
                {{ day }}
              </span>
            </div>
            <div class="date-picker-days">
              <span
                v-for="(day, index) in daysInMonth"
                :key="index"
                :class="[
                  'day',
                  {
                    today: isToday(day),
                    selected: isSelected(day),
                    'disabled-picker': isDisabled(day),
                  },
                ]"
                @click="selectDate(day)"
              >
                <span class="dot" v-if="isToday(day)"></span>
                {{ day }}
              </span>
            </div>
          </div>
          <hr class="hr mt-4" />
          <div class="date-picker-actions">
            <ExButton
              :outline="'outline-hover'"
              :label="'Cancel'"
              @onClick="finishPicker"
              size="normal"
              :fullwidth="'default'"
            />
            <ExButton
              :normal="
                !tempDate || isTempDateDisabled
                  ? 'normal-disable'
                  : 'normal-hover'
              "
              :disabled="!tempDate || isTempDateDisabled"
              :label="'Apply'"
              @onClick="applyDate"
              size="normal"
              :fullwidth="'default'"
              :loading="isApplyLoading ? 'left' : null"
            />
          </div>
        </div>
      </div>
      <div
        v-else
        ref="dropdownRef"
        class="date-picker-dropdown"
        :style="dropdownCalendarStyle"
      >
        <div class="date-picker-header">
          <div
            class="date-picker-icon"
            :class="{ 'disabled-icon': isPrevButtonDisabled }"
          >
            <img
              :src="require('@components/assets/left-icon-dark.svg')"
              alt=""
              class="nav-btn"
              @click="handlePrevMonth"
              :class="{ 'disabled-btn': isPrevButtonDisabled }"
            />
          </div>
          <span class="month-year">{{ monthYear }}</span>
          <div
            class="date-picker-icon"
            :class="{ 'disabled-icon': isNextButtonDisabled }"
          >
            <img
              :src="require('@components/assets/right-icon-dark.svg')"
              alt=""
              class="nav-btn"
              @click="handleNextMonth"
              :class="{ 'disabled-btn': isNextButtonDisabled }"
            />
          </div>
        </div>
        <div class="date-picker-search-bar">
          <ExButton
            class="mr-2"
            :outline="'outline-hover'"
            :label="'Today'"
            @onClick="selectToday"
            size="normal"
            :fullwidth="'default'"
          />
        </div>
        <div class="date-picker-calendar">
          <div class="date-picker-weekdays">
            <span v-for="day in weekdays" :key="day" class="weekday">
              {{ day }}
            </span>
          </div>
          <div class="date-picker-days">
            <span
              v-for="(day, index) in daysInMonth"
              :key="index"
              :class="[
                'day',
                { today: isToday(day), selected: isSelected(day) },
              ]"
              @click="selectDate(day)"
            >
              <span class="dot" v-if="isToday(day)"></span>
              {{ day }}
            </span>
          </div>
        </div>
        <hr class="hr mt-4" />
        <div class="date-picker-actions">
          <ExButton
            :outline="'outline-hover'"
            :label="'Cancel'"
            @onClick="closePicker"
            size="normal"
            :fullwidth="'default'"
          />
          <ExButton
            :normal="
              !tempDate || isTempDateDisabled
                ? 'normal-disable'
                : 'normal-hover'
            "
            :disabled="!tempDate || isTempDateDisabled"
            :label="'Apply'"
            @onClick="applyDate"
            size="normal"
            :fullwidth="'default'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  defineProps,
  defineEmits,
  watch,
  getCurrentInstance,
  nextTick,
} from "vue";
import Icon from "@components/icons/Icon.vue";
import ExButton from "@components/buttons/Button.vue";
import { useBodyPortalDropdown } from "@components/utils/useBodyPortalDropdown";

const props = defineProps({
  value: {
    type: String,
    default: null,
  },
  modal: {
    type: Boolean,
    default: false,
  },
  isDisableDatepicker: {
    type: Boolean,
    default: false,
  },
  isDatePickerOpen: {
    type: Boolean,
    default: false,
  },
  /** Opens the calendar immediately (e.g. editor slash → Date). No API loader on Apply. */
  autoOpen: {
    type: Boolean,
    default: false,
  },
  minDate: {
    type: String,
    default: null,
  },
  /** Inclusive upper bound (YYYY-MM-DD). Omit to leave future dates selectable. */
  maxDate: {
    type: String,
    default: null,
  },
  /** YYYY-MM-DD strings or (date: Date) => boolean. Applied in addition to min/max. */
  disabledDates: {
    type: [Array, Function],
    default: null,
  },
  changeBadgeIndicator: {
    type: Boolean,
    default: false,
  },
  isDirty: {
    type: Boolean,
    default: false,
  },
  customCalendarIcon: {
    type: Boolean,
    default: false,
  },
  dataPickerPlaceHolder: {
    type: String,
    default: "Select date",
  },
  showClearIcon: {
    type: Boolean,
    default: false,
  },
  /** Custom format for displayed date. e.g. 'dd-MMM-yyyy' for 1-Jan-2026 */
  dateFormat: {
    type: String,
    default: null,
  },
});

/**
 * Calendar-day parser. Date-only `YYYY-MM-DD` is local (not UTC midnight),
 * so min/max bounds do not shift by timezone.
 * @param {Date|string|null|undefined} value
 * @returns {Date|null}
 */
function parseDateInput(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7)) - 1;
    const day = Number(value.slice(8, 10));
    return new Date(year, month, day);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate()
  );
}

const emit = defineEmits(["onApplyDate", "onClose", "onClearSelection"]);
const app = getCurrentInstance();
const { proxy } = app;
const isApplyLoading = computed(() =>
  Boolean(
    props.isDatePickerOpen &&
      !props.autoOpen &&
      proxy?.$loader?.isAppLoading
  )
);
const isOpen = ref(false);
const modalOverlayRef = ref(null);
const dropdownRef = ref(null);
const containerRef = ref(null);
const displayButtonRef = ref(null);
const selectedDate = ref(null);
const tempDate = ref(null);
const currentMonth = ref(new Date().getMonth());
const currentYear = ref(new Date().getFullYear());

const weekdays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const togglePicker = () => {
  isOpen.value = !isOpen.value;
  if (selectedDate.value) {
    currentMonth.value = selectedDate.value.getMonth();
    currentYear.value = selectedDate.value.getFullYear();
    tempDate.value = selectedDate.value;
  }
};

const closePicker = () => {
  isOpen.value = false;
  tempDate.value = null;
  emit("onClose");
};

const CALENDAR_WIDTH = "300px";

const { dropdownStyle, attach, detach } = useBodyPortalDropdown({
  getAnchor: () => displayButtonRef.value || containerRef.value,
  getDropdown: () => dropdownRef.value,
  getContainer: () => containerRef.value,
  onClose: closePicker,
});

// The calendar keeps its own width; the composable would otherwise size it to the trigger button.
const dropdownCalendarStyle = computed(() => ({
  ...dropdownStyle.value,
  width: CALENDAR_WIDTH,
}));

const finishPicker = () => {
  if (props.modal) {
    cleanupOverlayPortal();
  }
  if (props.autoOpen && props.modal) {
    isOpen.value = false;
    tempDate.value = null;
    emit("onClose");
    return;
  }
  closePicker();
};

const clearSelection = () => {
  selectedDate.value = null;
  tempDate.value = null;
  emit("onClearSelection");
  emit("onApplyDate", null);
};

const formattedDate = computed(() => {
  const date = props?.value || selectedDate.value;
  if (!date) return null;
  const d = parseDateInput(date) || new Date(date);
  if (props.dateFormat === "dd-MMM-yyyy") {
    const day = d.getDate();
    const month = d.toLocaleDateString("en-US", { month: "short" });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
});

const monthYear = computed(() =>
  new Date(currentYear.value, currentMonth.value).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  })
);

const daysInMonth = computed(() => {
  const days = [];
  const firstDay = new Date(currentYear.value, currentMonth.value, 1).getDay();
  const totalDays = new Date(
    currentYear.value,
    currentMonth.value + 1,
    0
  ).getDate();

  for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
    days.push("");
  }
  for (let i = 1; i <= totalDays; i++) {
    days.push(i);
  }

  return days;
});

const isToday = (day) => {
  const today = new Date();
  return (
    day &&
    today.getDate() === day &&
    today.getMonth() === currentMonth.value &&
    today.getFullYear() === currentYear.value
  );
};
function toISODateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isDateDisabledByConfig(date) {
  if (!props.disabledDates || !date) return false;
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (typeof props.disabledDates === "function") {
    return Boolean(props.disabledDates(day));
  }
  if (Array.isArray(props.disabledDates)) {
    return props.disabledDates.includes(toISODateString(day));
  }
  return false;
}

function isDateOutsideBounds(date) {
  if (!date) return false;
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const min = parseDateInput(props.minDate);
  const max = parseDateInput(props.maxDate);
  if (min && day < min) return true;
  if (max && day > max) return true;
  return false;
}

function isDateUnselectable(date) {
  if (!date) return false;
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return isDateOutsideBounds(day) || isDateDisabledByConfig(day);
}

const isTodayDisabled = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return isDateOutsideBounds(today);
});
const isTempDateDisabled = computed(() => {
  if (!tempDate.value) return false;
  return isDateUnselectable(new Date(tempDate.value));
});

const isPrevButtonDisabled = computed(() => {
  const min = parseDateInput(props.minDate);
  if (!min) return false;

  const current = new Date(currentYear.value, currentMonth.value, 1);
  const minMonth = new Date(min.getFullYear(), min.getMonth(), 1);
  return current <= minMonth;
});

const isNextButtonDisabled = computed(() => {
  const max = parseDateInput(props.maxDate);
  if (!max) return false;
  const current = new Date(currentYear.value, currentMonth.value, 1);
  const maxMonth = new Date(max.getFullYear(), max.getMonth(), 1);
  return current >= maxMonth;
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      const parsed = parseDateInput(newVal);
      if (parsed) {
        selectedDate.value = parsed;
        tempDate.value = parsed;
        currentMonth.value = parsed.getMonth();
        currentYear.value = parsed.getFullYear();
      }
    } else {
      selectedDate.value = null;
      tempDate.value = null;
    }
  }
);
const isSelected = (day) =>
  day &&
  tempDate.value &&
  tempDate.value.getDate() === day &&
  tempDate.value.getMonth() === currentMonth.value &&
  tempDate.value.getFullYear() === currentYear.value;

const selectDate = (day) => {
  if (!day || isDisabled(day)) {
    return;
  }
  tempDate.value = new Date(currentYear.value, currentMonth.value, day);
};

const selectToday = () => {
  const today = new Date();
  currentMonth.value = today.getMonth();
  currentYear.value = today.getFullYear();
  tempDate.value = today;
};

const prevMonth = () => {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value -= 1;
  } else {
    currentMonth.value -= 1;
  }
};

const nextMonth = () => {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value += 1;
  } else {
    currentMonth.value += 1;
  }
};

const applyDate = () => {
  selectedDate.value = tempDate.value;
  const loader = proxy?.$loader;
  const useAssignmentLoader = props?.isDatePickerOpen && !props?.autoOpen;
  if (useAssignmentLoader && loader) {
    loader.setAppLoading(true);
  }
  emit("onApplyDate", tempDate.value);
  if (useAssignmentLoader && loader) {
    loader.setAppLoading(false);
  }
  finishPicker();
};

if (props.value) {
  const parsed = parseDateInput(props.value);
  if (parsed) {
    selectedDate.value = parsed;
    tempDate.value = parsed;
    currentMonth.value = parsed.getMonth();
    currentYear.value = parsed.getFullYear();
  }
}
const isDisabled = (day) => {
  if (!day) return false;
  return isDateUnselectable(
    new Date(currentYear.value, currentMonth.value, day)
  );
};

const handlePrevMonth = () => {
  if (!isPrevButtonDisabled.value) {
    prevMonth();
  }
};

const handleNextMonth = () => {
  if (!isNextButtonDisabled.value) {
    nextMonth();
  }
};

const shouldOpenProgrammatically = () =>
  Boolean(props?.isDatePickerOpen || props?.autoOpen);

const portalOverlayToBody = () => {
  if (!props.modal) {
    return;
  }

  nextTick(() => {
    const overlay = modalOverlayRef.value;
    if (overlay && overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }
  });
};

const cleanupOverlayPortal = () => {
  const overlay = modalOverlayRef.value;
  if (overlay?.parentNode === document.body) {
    document.body.removeChild(overlay);
  }
};

onMounted(() => {
  if (props.modal && shouldOpenProgrammatically()) {
    document.addEventListener("keydown", handleModalEscape, true);
  }
  if (isOpen.value) {
    portalOverlayToBody();
    if (!props.modal) attach();
  }
});
onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleModalEscape, true);
  cleanupOverlayPortal();
});
watch(isOpen, (open) => {
  if (open) {
    portalOverlayToBody();
    if (!props.modal) attach();
  } else {
    detach();
  }
});
watch(
  () => [props?.isDatePickerOpen, props?.autoOpen],
  (newVal, oldVal) => {
    const [isPickerOpen, autoOpen] = newVal || [];
    const [wasPickerOpen, wasAutoOpen] = oldVal || [];
    const open = Boolean(isPickerOpen || autoOpen);
    const wasOpen = Boolean(wasPickerOpen || wasAutoOpen);
    if (open) {
      isOpen.value = true;
      return;
    }
    if (wasOpen && !open) {
      isOpen.value = false;
    }
  },
  { immediate: true }
);

const handleModalEscape = (event) => {
  if (event.key !== "Escape" || !isOpen.value || !props.modal) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  finishPicker();
};
</script>
<style scoped>
.date-picker-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.date-picker-modal-overlay--programmatic {
  z-index: 100001;
}
.date-picker-modal {
  background: white;
  border-radius: 8px;
  padding: 20px;
  min-width: 320px;
  max-width: 95vw;
  font-family: Inter !important;
  pointer-events: auto;
}
</style>
<style scoped>
.date-picker-container {
  position: relative;
}

.date-picker-display {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  gap: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  font-family: Inter !important;
}
.date-picker-display-open {
  border-radius: var(--radius-md, 8px);
  border: 1px solid #d6bbfb;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #e6ddf9;
}
.date-picker-display-dirty {
  background: #d6bbfb;
}

/* Moved to <body> by useBodyPortalDropdown; position, width and z-index come from its bound style */
.date-picker-dropdown {
  padding: 16px 0px;
  border-radius: var(--radius-xl, 12px);
  border: 1px solid var(--Colors-Border-border-secondary, #eaecf0);
  background: var(--Colors-Background-bg-primary, #fff);

  /* Shadows/shadow-xl */
  box-shadow: 0px 20px 24px -4px rgba(16, 24, 40, 0.08),
    0px 8px 8px -4px rgba(16, 24, 40, 0.03);
}

.date-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  margin-bottom: 12px;
  padding: 0px 16px;
}
.date-picker-search-bar {
  margin-bottom: 12px;
  padding: 0px 16px;
}
.date-picker-calendar {
  padding: 0px 16px;
}
.date-picker-icon {
  height: 24px;
  width: 24px;
  border-radius: 100%;
  display: flex;
  justify-items: center;
  align-items: center;
}
.date-picker-icon:hover {
  border-radius: 100%;
  cursor: url("../assets/cursorArrow.svg"), auto;
  background: #f4f4f4;
}
.date-picker-icon.disabled-icon {
  opacity: 0.5;
  cursor: not-allowed;
}
.date-picker-icon.disabled-icon:hover {
  background: none;
  cursor: not-allowed;
}
.date-picker-icon img {
  margin-left: 2px;
}
.date-picker-icon img.disabled-btn {
  opacity: 0.5;
  cursor: not-allowed;
}

.date-picker-weekdays,
.date-picker-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}
.month-year {
  color: var(--colors-foreground-fg-secondary-700, #344054);
  text-align: center;

  /* Text md/Semibold */
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}

.weekday {
  margin-bottom: 8px;
  color: #344054;
  text-align: center;

  /* Text sm/Medium */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
}

.day {
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  position: relative;
  color: #344054;
  text-align: center;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
.day:hover {
  color: #182230;
  text-align: center;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  border-radius: 9999px;
  background: #f9fafb;
}

.day.today::after {
  content: "";
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 5px;
  flex-shrink: 0;
  border-radius: 100%;
  background: #7f56d9;
}

.day.selected {
  border-radius: var(--radius-full, 9999px);
  background: var(--Primary, #7f56d9);
  color: #fff;
  text-align: center;

  /* Text sm/Medium */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  display: flex;
  justify-content: center;
  align-items: center;
}

.date-picker-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  padding: 0px 16px;
  gap: 12px;
}
.date-picker-dropdown .hr {
  border: 1px solid #f2f4f7;
}
.disabled-picker {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}
.day.disabled-picker {
  color: #d0d5dd;
  pointer-events: none;
  background: none !important;
  cursor: not-allowed;
  opacity: 0.5;
}
.append-icon-hover {
  cursor: pointer;
}
</style>
